import React, { useState, useEffect, useMemo, useContext } from 'react';
import api from '../api';
import { MonthContext } from '../context/MonthContext';
import { Search, Download, Send, RefreshCw, FileText } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const defaultCols = [
    { id: 'account_name', label: 'Libellé (compte)', width: '210px' },
    { id: 'period', label: 'Date (mois)', width: '140px' },
    { id: 'file_name', label: 'Nom du Fichier', width: '340px' },
    { id: 'file_size', label: 'Taille', width: '110px' },
    { id: 'actions', label: 'Actions', width: '190px' }
];

const MOIS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
const formatPeriod = (p) => {
    if (!p) return '';
    const [m, y] = String(p).split('-'); // Qonto: "MM-YYYY"
    const i = parseInt(m, 10) - 1;
    return (MOIS_FR[i] || m) + ' ' + (y || '');
};

const SortableHeader = ({ col, sortBy, sortDir, filters, setFilters, handleSort, width, onResize }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: width || '150px',
        minWidth: width || '150px',
        maxWidth: width || '150px',
        padding: '0.75rem',
        background: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
        color: '#475569',
        fontWeight: 600,
        fontSize: '0.875rem',
        textAlign: 'left',
        position: 'relative',
        zIndex: transform ? 1 : 0
    };

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [col.id]: e.target.value }));
    };

    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.pageX;
        const startWidth = parseInt(width.replace('px', '')) || 150;

        const handleMouseMove = (moveEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
            onResize(`${newWidth}px`);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <th ref={setNodeRef} style={style}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexGrow: 1 }} onClick={() => handleSort(col.id)}>
                    <div {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: '4px', display: 'flex', alignItems: 'center' }}>⋮⋮</div>
                    <span style={{ textTransform: 'uppercase', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.label}</span>
                    {sortBy === col.id && (<span>{sortDir === 'asc' ? '↑' : '↓'}</span>)}
                </div>
                {col.id !== 'actions' && (
                    <input type="text" className="input-field" placeholder="Filtrer..." value={filters[col.id] || ''} onChange={handleFilterChange} style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '4px' }} />
                )}
            </div>
            <div onMouseDown={handleResizeMouseDown} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', background: 'transparent' }} />
        </th>
    );
};

export default function BankStatements() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('period');
    const [sortDir, setSortDir] = useState('desc');
    
    // Pagination
    const [page, setPage] = useState(1);
    const limit = 100;

    // Colonnes
    const [activeCols, setActiveCols] = useState(defaultCols.map(c => c.id));
    const [colWidths, setColWidths] = useState({});

    // Modal Email
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedStatement, setSelectedStatement] = useState(null);
    const [emailTo, setEmailTo] = useState('compta@septimeo.com');
    const [emailCc, setEmailCc] = useState('gdejabrun@tallem.fr');
    const [sending, setSending] = useState(false);
    const { selectedMonth } = useContext(MonthContext);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkMode, setBulkMode] = useState(false);

    useEffect(() => {
        const savedCols = localStorage.getItem('bank_statements_cols_v2');
        const savedWidths = localStorage.getItem('bank_statements_widths_v2');
        if (savedCols) setActiveCols(JSON.parse(savedCols));
        if (savedWidths) setColWidths(JSON.parse(savedWidths));
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/qonto/statements');
            if (res.data && res.data.data) {
                setData(res.data.data);
            }
        } catch (e) {
            console.error("Erreur chargement relevés", e);
        } finally {
            setLoading(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setActiveCols((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newCols = arrayMove(items, oldIndex, newIndex);
                localStorage.setItem('bank_statements_cols_v2', JSON.stringify(newCols));
                return newCols;
            });
        }
    };

    const handleResizeColumn = (colId, newWidth) => {
        setColWidths(prev => {
            const updated = { ...prev, [colId]: newWidth };
            localStorage.setItem('bank_statements_widths_v2', JSON.stringify(updated));
            return updated;
        });
    };

    const handleSort = (id) => {
        if (id === 'actions') return;
        if (sortBy === id) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(id);
            setSortDir('asc');
        }
    };

    const filteredData = useMemo(() => {
        let result = [...data];

        // 0. Filtre sur le mois du menu (period Qonto = "MM-YYYY")
        if (selectedMonth && selectedMonth !== 'all') {
            const [yy, mm] = selectedMonth.split('-');
            const monthKey = `${mm}-${yy}`;
            result = result.filter(r => r.period === monthKey);
        }

        // 1. Recherche globale
        if (search) {
            const lowerSearch = search.toLowerCase();
            result = result.filter(r => 
                (r.period && r.period.toLowerCase().includes(lowerSearch)) ||
                (r.file?.file_name && r.file.file_name.toLowerCase().includes(lowerSearch))
            );
        }

        // 2. Filtres colonnes
        Object.keys(filters).forEach(key => {
            const val = filters[key]?.toLowerCase();
            if (val) {
                if (val === 'vide') {
                    result = result.filter(r => !r[key] || r[key] === '' || r[key] === null);
                } else {
                    result = result.filter(r => {
                        let text = '';
                        if (key === 'file_name') text = r.file?.file_name || '';
                        else if (key === 'file_size') text = String(r.file?.file_size || '');
                        else text = String(r[key] || '');
                        return text.toLowerCase().includes(val);
                    });
                }
            }
        });

        // 3. Tri
        result.sort((a, b) => {
            let valA, valB;
            if (sortBy === 'file_name') { valA = a.file?.file_name; valB = b.file?.file_name; }
            else if (sortBy === 'file_size') { valA = a.file?.file_size; valB = b.file?.file_size; }
            else { valA = a[sortBy]; valB = b[sortBy]; }

            if (!valA) valA = '';
            if (!valB) valB = '';

            if (sortBy === 'file_size') { valA = Number(valA); valB = Number(valB); }

            if (valA < valB) return sortDir === 'asc' ? -1 : 1;
            if (valA > valB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [data, search, filters, sortBy, sortDir, selectedMonth]);

    const total = filteredData.length;
    const currentData = filteredData.slice((page - 1) * limit, page * limit);

    // Sélection multiple (pour envoi groupé en un seul mail)
    const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const allVisibleSelected = currentData.length > 0 && currentData.every(r => selectedIds.includes(r.id));
    const toggleSelectAll = () => {
        if (allVisibleSelected) setSelectedIds(prev => prev.filter(id => !currentData.some(r => r.id === id)));
        else setSelectedIds(prev => [...new Set([...prev, ...currentData.map(r => r.id)])]);
    };
    const selectedStatements = filteredData.filter(s => selectedIds.includes(s.id));

    const exportExcel = () => {
        let csv = '\uFEFF'; // BOM for Excel
        csv += 'ID;Période;Nom du fichier;Taille (octets)\n';
        
        filteredData.forEach(r => {
            const row = [
                r.id || '',
                r.period || '',
                r.file?.file_name || '',
                r.file?.file_size || 0
            ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(';');
            csv += row + '\n';
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Releves_Qonto_${new Date().toISOString().slice(0,10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openBulkModal = () => {
        if (selectedIds.length === 0) return;
        setBulkMode(true);
        setSelectedStatement(null);
        setShowEmailModal(true);
    };

    const closeModal = () => {
        setShowEmailModal(false);
        setSelectedStatement(null);
        setBulkMode(false);
    };

    const handleSendEmail = async () => {
        setSending(true);
        try {
            if (bulkMode) {
                // Envoi groupé : tous les relevés sélectionnés dans UN SEUL mail (endpoint pluriel)
                const ids = selectedStatements.map(s => s.id);
                if (ids.length === 0) { setSending(false); return; }
                const monthLabel = selectedMonth && selectedMonth !== 'all' ? selectedMonth : (selectedStatements[0]?.period || 'sélection');
                await api.post('/email/send-statements', {
                    month_year: monthLabel,
                    to: emailTo,
                    cc: emailCc,
                    remarques: '',
                    statementIds: ids
                });
                setSelectedIds([]);
            } else {
                if (!selectedStatement) { setSending(false); return; }
                await api.post('/qonto/send-statement', {
                    period: selectedStatement.period,
                    file_url: selectedStatement.file?.file_url,
                    file_name: selectedStatement.file?.file_name,
                    to: emailTo,
                    cc: emailCc
                });
            }
            alert("Email envoyé avec succès !");
            closeModal();
        } catch (e) {
            alert("Erreur: " + (e.response?.data?.error || e.message));
        } finally {
            setSending(false);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const columns = activeCols.map(id => defaultCols.find(c => c.id === id)).filter(Boolean);

    const renderPagination = () => (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: '0.25rem 0.5rem' }}>Précédent</button>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Page {page} / {Math.max(1, Math.ceil(total / limit))}</span>
            <button className="btn" disabled={page * limit >= total} onClick={() => setPage(page + 1)} style={{ padding: '0.25rem 0.5rem' }}>Suivant</button>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                        <FileText size={28} color="var(--brand)" />
                        Relevés Bancaires Qonto
                    </h1>
                    <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Consultez et envoyez vos relevés mensuels Qonto.</p>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                        <input 
                            type="text" 
                            className="input-field" 
                            placeholder="Recherche globale..." 
                            value={search} 
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ paddingLeft: '35px', width: '250px' }}
                            title="Recherche sur l'ID, Période et Fichier"
                        />
                    </div>
                    <button className="btn" onClick={exportExcel} style={{ background: '#107c41', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Download size={16} /> Excel
                    </button>
                    <button className="btn" onClick={loadData} disabled={loading} style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                    </button>
                    <button className="btn" onClick={openBulkModal} disabled={selectedIds.length === 0}
                        style={{ background: selectedIds.length ? 'var(--brand)' : 'var(--bg-secondary)', color: selectedIds.length ? 'white' : 'var(--text-secondary)', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, opacity: selectedIds.length ? 1 : 0.6, cursor: selectedIds.length ? 'pointer' : 'not-allowed' }}>
                        <Send size={16} /> Envoyer la sélection ({selectedIds.length})
                    </button>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600, background: 'var(--bg-secondary)', padding: '0.5rem 1rem', borderRadius: '8px' }}>
                        {total} ligne(s)
                    </div>
                    {renderPagination()}
                </div>
            </div>

            <div className="glass-panel data-table-wrapper" style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column', flex: 1, padding: 0 }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <table style={{ minWidth: 'max-content', width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <SortableContext items={activeCols} strategy={horizontalListSortingStrategy}>
                                <tr>
                                    <th style={{ width: '42px', minWidth: '42px', padding: '0.75rem', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'center' }}>
                                        <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Tout sélectionner" />
                                    </th>
                                    {columns.map(col => (
                                        <SortableHeader
                                            key={col.id} col={col} width={colWidths[col.id] || col.width || '150px'}
                                            onResize={(newWidth) => handleResizeColumn(col.id, newWidth)}
                                            sortBy={sortBy} sortDir={sortDir} filters={filters} setFilters={setFilters}
                                            handleSort={handleSort}
                                        />
                                    ))}
                                </tr>
                            </SortableContext>
                        </thead>
                        <tbody>
                            {!loading && currentData.length === 0 ? (
                                <tr><td colSpan={columns.length + 1} style={{ padding: '2rem', textAlign: 'center' }}>Aucun relevé trouvé.</td></tr>
                            ) : !loading && currentData.map(row => (
                                <tr key={row.id} style={{ borderBottom: '1px solid var(--glass-border)', background: selectedIds.includes(row.id) ? 'rgba(56,189,248,0.06)' : 'transparent' }} className="hover-row">
                                    <td style={{ width: '42px', padding: '1rem', textAlign: 'center' }}>
                                        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                                    </td>
                                    {columns.map(col => {
                                        let content = null;
                                        if (col.id === 'account_name') content = <span style={{ fontWeight: 500 }}>{row.account_name || '—'}</span>;
                                        else if (col.id === 'period') content = <span style={{ fontWeight: 600 }}>{formatPeriod(row.period)}</span>;
                                        else if (col.id === 'file_name') {
                                            content = (
                                                <a href={row.file?.file_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--brand)', textDecoration: 'none', fontWeight: 500 }} className="hover-underline">
                                                    <Download size={14} />
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.file?.file_name}</span>
                                                </a>
                                            );
                                        }
                                        else if (col.id === 'file_size') content = formatBytes(row.file?.file_size);
                                        else if (col.id === 'actions') {
                                            content = (
                                                <button 
                                                    className="btn" 
                                                    style={{ padding: '0.35rem 0.75rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontWeight: 600 }}
                                                    onClick={() => { setSelectedStatement(row); setShowEmailModal(true); }}
                                                >
                                                    <Send size={14} /> Envoyer Comptable
                                                </button>
                                            );
                                        }

                                        return (
                                            <td key={col.id} style={{ padding: '1rem', width: colWidths[col.id] || col.width || '150px', minWidth: colWidths[col.id] || col.width || '150px', maxWidth: colWidths[col.id] || col.width || '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {content}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {loading && (
                                <tr>
                                    <td colSpan={columns.length + 1} style={{ padding: '2rem', textAlign: 'center' }}>
                                        <RefreshCw size={24} className="spinning" style={{ color: 'var(--brand)' }} />
                                        <div style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Chargement des relevés Qonto...</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </DndContext>
            </div>

            <div style={{ alignSelf: 'center' }}>{renderPagination()}</div>

            {/* Modal Envoi Email */}
            {showEmailModal && (selectedStatement || bulkMode) && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                    <div className="glass-panel" style={{ width: '500px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', position: 'relative' }}>
                        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Send size={20} color="var(--brand)" />
                            {bulkMode
                              ? `Envoyer ${selectedStatements.length} relevé${selectedStatements.length > 1 ? 's' : ''} au comptable`
                              : `Envoyer le relevé ${formatPeriod(selectedStatement.period)}`}
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 500 }}>À (Séparés par des virgules)</label>
                                <input 
                                    type="text" 
                                    className="input-field" 
                                    value={emailTo} 
                                    onChange={(e) => setEmailTo(e.target.value)}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px' }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 500 }}>Cc</label>
                                <input 
                                    type="text" 
                                    className="input-field" 
                                    value={emailCc} 
                                    onChange={(e) => setEmailCc(e.target.value)}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px' }}
                                />
                            </div>
                            
                            <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--glass-border)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                {bulkMode ? (
                                    <>
                                        <p style={{ margin: '0 0 0.5rem 0' }}><strong>{selectedStatements.length} pièce(s) jointe(s) :</strong></p>
                                        <ul style={{ margin: 0, paddingLeft: '1.2rem', maxHeight: '140px', overflowY: 'auto' }}>
                                            {selectedStatements.map(s => <li key={s.id}>{s.account_name ? s.account_name + ' — ' : ''}{s.file?.file_name}</li>)}
                                        </ul>
                                    </>
                                ) : (
                                    <p style={{ margin: '0 0 0.5rem 0' }}><strong>Pièce jointe :</strong> {selectedStatement.file?.file_name}</p>
                                )}
                                <p style={{ margin: '0.5rem 0 0 0' }}>Un email stylisé avec le logo Tallem sera envoyé contenant {bulkMode ? 'ces relevés PDF' : 'ce relevé PDF'}.</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                            <button className="btn" onClick={closeModal} disabled={sending} style={{ background: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-primary)' }}>
                                Annuler
                            </button>
                            <button className="btn" onClick={handleSendEmail} disabled={sending} style={{ background: 'var(--brand)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {sending ? <RefreshCw size={16} className="spinning" /> : <Send size={16} />} 
                                {sending ? 'Envoi en cours...' : 'Envoyer maintenant'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
